/**
 * The mirrored Bexio invoice list (`/admin/bexio/invoices`).
 *
 * NOT the draft workflow. "Rechnungen" is the read-only mirror of what already
 * exists in Bexio; "Rechnungsentwürfe" (`/drafts`) is the C5 creation flow. This
 * page never creates, cancels, sends or edits anything — Bexio is the system of
 * record for invoice status and payment, and the mirror is read-only.
 *
 * REQUEST SHAPE. The list endpoint paginates server-side and counts `total` over
 * the same filters, so this page holds exactly one page at a time. That is the
 * deliberate contrast with the legacy page, which paged through up to 40 requests
 * / 2000 invoices on mount to fill one table. Two further requests are made once
 * per page load: the reconciliation summary for the KPI row, and one BATCH
 * mapping-status call for the ids on the current page. Line items are fetched
 * only when a drawer opens, for that one invoice.
 *
 * WHAT THE DESIGN REFERENCE ASKS FOR AND THIS DOES NOT HAVE: an "In Bexio öffnen"
 * button. No Bexio deep link exists anywhere in this system — `network_link` has
 * zero occurrences, and the only Bexio URL is the server-to-server API host. A
 * link built from the invoice id would be a guess at another product's URL
 * scheme, so the button is absent rather than broken.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Wallet, CheckCircle2, AlertTriangle } from "lucide-react";

import {
  fetchBexioInvoiceMapping,
  fetchBexioInvoicePositions,
  fetchBexioInvoices,
  fetchBexioInvoiceMappingStatuses,
  fetchBexioReconciliation,
} from "../../../api/adminData/bexio";
import { formatDateTime } from "../../../utils/datetime";
import BexioKpiFilterCard from "../../../components/admin/bexio/workspace/BexioKpiFilterCard";
import BexioActiveFilters from "../../../components/admin/bexio/workspace/BexioActiveFilters";
import BexioInvoiceMappingModal from "../../../components/admin/bexio/mapping/BexioInvoiceMappingModal";
import StatusPill from "../../../components/admin/bexio/BexioInvoiceStatusPill";
import BexioFilterBar, {
  BexioFilterSelect,
} from "../../../components/admin/bexio/workspace/BexioFilterBar";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import BexioDetailDrawer, {
  DrawerField,
  DrawerSection,
} from "../../../components/admin/bexio/workspace/BexioDetailDrawer";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
import { useBexioList } from "../../../components/admin/bexio/workspace/useBexioList";
import { useDebouncedValue } from "../../../components/admin/bexio/workspace/useDebouncedValue";
import {
  INVOICE_STATUS_LABEL,
  MAPPING_STATUS_LABEL,
  PROVIDER_MISSING_EXPLANATION,
  PROVIDER_MISSING_LABEL,
  PROVIDER_MISSING_LABEL_SHORT,
  currencyLabel,
  fmtInvoiceMoney,
  fmtMoney,
  mapLabel,
  mappingTone,
} from "../../../components/admin/bexio/bexioFormat";

/** Exactly the statuses the endpoint accepts; an unknown one is a 422. */
const STATUS_OPTIONS = [
  { value: "", label: "Status: Alle" },
  { value: "pending", label: "Ausstehend" },
  { value: "partial", label: "Teilweise bezahlt" },
  { value: "paid", label: "Bezahlt" },
  { value: "draft", label: "Entwurf" },
  { value: "canceled", label: "Storniert" },
  { value: "unknown", label: "Unbekannt" },
];

/**
 * `status` is a LIST because the "Offen" KPI stands for two Bexio statuses
 * (pending + partial) and must write the same field the dropdown writes — two
 * fields would be two competing truths, which is the state §22 forbids.
 */
const EMPTY_FILTERS = { q: "", status: [], mapping: "", providerPresence: "" };

/**
 * The provider-presence dropdown.
 *
 * "Nicht als fehlend markiert" rather than "In Bexio vorhanden", because the second
 * would claim more than the data supports: the filter selects rows without a
 * missing-mark, and most of those have simply never been examined. The API's own
 * docstring draws the same line, and a label that quietly widened it here would be
 * the place the misunderstanding starts.
 */
const PRESENCE_OPTIONS = [
  { value: "", label: "Alle" },
  { value: "missing", label: PROVIDER_MISSING_LABEL_SHORT },
  { value: "present", label: "Nicht als fehlend markiert" },
];

/** The KPI categories, each carrying the exact filter it stands for. */
const KPI_FILTERS = {
  all: {},
  open: { status: ["pending", "partial"] },
  paid: { status: ["paid"] },
  review: { mapping: "review" },
};

/** Which KPI, if any, the current filter state corresponds to. */
function activeKpi(filters) {
  // A presence filter belongs to no card. Without this the "Gesamt Rechnungen"
  // card would light up while the table showed a narrowed set — the card claiming
  // to be the active view of something it is not.
  if (filters.providerPresence) return null;
  if (filters.mapping === "review") return "review";
  const st = filters.status || [];
  if (st.length === 2 && st.includes("pending") && st.includes("partial")) return "open";
  if (st.length === 1 && st[0] === "paid") return "paid";
  if (st.length === 0 && !filters.mapping) return "all";
  return null;
}

/**
 * The SECOND truth on a row, never a replacement for the first.
 *
 * A provider-missing invoice keeps its mirrored status: it may still be `pending`
 * and it may still be `canceled`, and both remain facts about what Bexio last told
 * us. What changed is a different dimension — whether the provider still holds the
 * document at all — so it gets its own pill beside the status rather than
 * overwriting it. Replacing "Offen" with "Gelöscht" would destroy the very
 * information an operator needs to understand the row.
 *
 * Amber, not red: nothing here is broken. The row is correct history, and the
 * financial readers have already stopped counting it.
 */
function ProviderPresencePill() {
  return (
    <span
      className="inline-flex whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      title={PROVIDER_MISSING_LABEL}
    >
      {PROVIDER_MISSING_LABEL_SHORT}
    </span>
  );
}

/** The mark, and nothing else. Never the status, the tenancy or the sync time. */
function isProviderMissing(invoice) {
  return invoice?.provider_missing_at != null;
}

function MappingPill({ status }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const tone = mappingTone(status);
  const cls = {
    good: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
    warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    bad: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
    neutral: "border-border bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${
        cls[tone] || cls.neutral
      }`}
    >
      {mapLabel(MAPPING_STATUS_LABEL, status)}
    </span>
  );
}

/**
 * What the row's mapping button offers, by current state.
 *
 * Distinct verbs on purpose: "Zuordnen" for a missing mapping, "Prüfen" for one
 * the backend flagged as unresolvable, "Ändern" for a valid one. A single generic
 * label would hide which of the three an operator is about to do.
 */
function mappingActionLabel(status) {
  if (status === "mapped") return "Ändern";
  if (status === "invalid") return "Prüfen";
  return "Zuordnen";
}

function fmtDate(value) {
  if (!value) return "—";
  const [y, m, d] = String(value).split("-");
  return y && m && d ? `${d}.${m}.${y}` : String(value);
}

export default function BexioInvoicesPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);

  // The input holds `filters.q` for responsiveness; only this lagged copy reaches
  // the server, which searches with an unindexed LIKE.
  const debouncedQ = useDebouncedValue(filters.q);

  const { items, total, loading, error, reload } = useBexioList({
    fetcher: fetchBexioInvoices,
    // Empty strings are dropped by the API client, so "no filter" and "cleared
    // filter" produce the same request.
    filters: {
      q: debouncedQ || undefined,
      status: filters.status?.length ? filters.status : undefined,
      mapping: filters.mapping || undefined,
      // Server-side, like every other filter here: the endpoint applies it before
      // the page is cut, so `total` counts the filtered set. Filtering the loaded
      // page instead would promise a count it does not show.
      provider_presence: filters.providerPresence || undefined,
    },
    limit,
    offset,
  });

  // KPI row. One reconciliation call: it is the only place that already knows the
  // per-status counts and the money totals for the whole org, so the cards are
  // org-wide figures rather than something summed from the visible page.
  const [recon, setRecon] = useState(null);
  const [reconError, setReconError] = useState("");
  const loadRecon = useCallback(async () => {
    setReconError("");
    try {
      setRecon(await fetchBexioReconciliation());
    } catch (e) {
      setRecon(null);
      setReconError(e?.message || "Kennzahlen konnten nicht geladen werden.");
    }
  }, []);
  useEffect(() => {
    loadRecon();
  }, [loadRecon]);

  // Mapping badges for the current page only — one batched call, never per row.
  const [mappingByInvoice, setMappingByInvoice] = useState({});
  useEffect(() => {
    const ids = (items || []).map((i) => i.id).filter(Boolean);
    if (!ids.length) {
      setMappingByInvoice({});
      return;
    }
    let alive = true;
    fetchBexioInvoiceMappingStatuses(ids)
      .then((res) => {
        if (!alive) return;
        const map = {};
        (res?.items || []).forEach((it) => {
          map[it.invoice_id] = it;
        });
        setMappingByInvoice(map);
      })
      .catch(() => {
        // Badges are context; the rows stand without them.
        if (alive) setMappingByInvoice({});
      });
    return () => {
      alive = false;
    };
  }, [items]);

  // Line items, lazily, for the open drawer only.
  const [positions, setPositions] = useState(null);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState("");
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    setPositions(null);
    setPositionsError("");
    setPositionsLoading(true);
    fetchBexioInvoicePositions(selected.id)
      .then((res) => {
        if (alive) setPositions(res);
      })
      .catch((e) => {
        if (alive) setPositionsError(e?.message || "Positionen nicht verfügbar.");
      })
      .finally(() => {
        if (alive) setPositionsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selected]);

  /**
   * The open invoice's mapping DETAIL, lazily.
   *
   * The batch call above is for badges only: its schema states that "batch
   * responses leave them None (status badges only, no N+1)", so the tenancy,
   * tenant, unit and room labels are absent from every batch item — including
   * mapped ones. Reading them off the batch result would leave the drawer's
   * Zuordnung section permanently empty and, worse, make a resolvable mapping
   * look like a data-quality problem. The single-invoice endpoint is the one that
   * populates them, and one request per opened drawer is not an N+1.
   */
  const [mappingDetail, setMappingDetail] = useState(null);
  const [mappingDetailLoading, setMappingDetailLoading] = useState(false);
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    setMappingDetail(null);
    setMappingDetailLoading(true);
    fetchBexioInvoiceMapping(selected.id)
      .then((res) => {
        if (alive) setMappingDetail(res);
      })
      .catch(() => {
        // Badge from the batch still stands; only the labels are missing.
        if (alive) setMappingDetail(null);
      })
      .finally(() => {
        if (alive) setMappingDetailLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selected]);

  /** Any filter change resets to page 1: page 4 of the old result set is meaningless. */
  const applyFilters = (next) => {
    setFilters(next);
    setOffset(0);
  };

  /**
   * Activate a KPI category.
   *
   * The search term survives — narrowing to "Zu prüfen" and then searching within
   * it is the point — but the previous category does not, so the status dropdown
   * and the cards can never disagree about what is filtered.
   */
  const selectKpi = (key) => {
    applyFilters({ ...EMPTY_FILTERS, q: filters.q, ...KPI_FILTERS[key] });
  };

  /** The invoice whose mapping modal is open. One modal for the whole page. */
  const [mappingInvoice, setMappingInvoice] = useState(null);

  /**
   * After a mapping write, refresh what the write actually changed.
   *
   * Three things move: the row's badge, the KPI counts, and — under an active
   * "Zu prüfen" filter — whether the invoice still belongs in the list at all. So
   * the list and the reconciliation are refetched and the mapping badges follow
   * the new rows. Nothing is assumed locally: the server decides whether a mapping
   * counts as complete, and an optimistic badge could disagree with it.
   */
  const handleMappingChanged = useCallback(() => {
    setMappingInvoice(null);
    reload();
    loadRecon();
  }, [reload, loadRecon]);

  const rows = items || [];
  const isFiltered = Boolean(
    filters.q || filters.status?.length || filters.mapping || filters.providerPresence
  );
  const kpi = activeKpi(filters);

  /** Visible chips for whatever is currently narrowing the table. */
  const chips = [
    filters.mapping === "review" && {
      key: "review",
      label: "Zu prüfen",
      onRemove: () => applyFilters({ ...filters, mapping: "" }),
    },
    filters.status?.length && {
      key: "status",
      label:
        filters.status.length > 1
          ? "Offen (ausstehend + teilweise)"
          : mapLabel(INVOICE_STATUS_LABEL, filters.status[0]),
      onRemove: () => applyFilters({ ...filters, status: [] }),
    },
    filters.providerPresence && {
      key: "providerPresence",
      label:
        PRESENCE_OPTIONS.find((o) => o.value === filters.providerPresence)?.label ||
        filters.providerPresence,
      onRemove: () => applyFilters({ ...filters, providerPresence: "" }),
    },
    filters.q && {
      key: "q",
      label: `Suche „${filters.q}“`,
      onRemove: () => applyFilters({ ...filters, q: "" }),
    },
  ];
  // What the current rows were actually fetched with.
  const queryIsFiltered = Boolean(
    debouncedQ || filters.status?.length || filters.mapping || filters.providerPresence
  );
  const counts = recon?.status_counts;
  const money = recon?.summary;
  const coverage = recon?.mapping_coverage;
  const currency = money?.currency || "CHF";

  const kpis = useMemo(() => {
    // The BACKEND's counted-open count, not a sum of mirror status counts.
    //
    // `status_counts` is the technical distribution over EVERY mirrored invoice, so
    // a provider-missing `pending` row is in it — while the amount below the card
    // comes from the financial readers, which exclude that row. The card showed the
    // two side by side and they disagreed. `open_invoice_count_counted` is computed
    // in the same loop that produces `open_total`, so the two cannot drift.
    //
    // Deliberately NOT falling back to `pending + partial` when the field is absent:
    // that fallback IS the bug, and a silent return to it would be invisible.
    const openCount = money?.open_invoice_count_counted ?? null;
    const review =
      coverage == null
        ? null
        : (coverage.unmapped || 0) + (coverage.partial_maybe_invalid || 0);
    return { openCount, review };
  }, [money, coverage]);

  const selectedMapping = selected ? mappingByInvoice[selected.id] : null;

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-foreground">Bexio-Rechnungen</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Synchronisierte Rechnungen aus Bexio verwalten und prüfen.
        </p>
      </header>

      {/* KPI row — org-wide figures from the reconciliation endpoint. */}
      {reconError ? (
        <div className="mb-4">
          <BexioTableError message={reconError} onRetry={loadRecon} />
        </div>
      ) : (
        <section
          aria-label="Kennzahlen"
          className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
        >
          <BexioKpiFilterCard
            icon={FileText}
            tone="primary"
            label="Gesamt Rechnungen"
            value={coverage?.total ?? "—"}
            hint="Alle synchronisierten Rechnungen"
            active={kpi === "all"}
            onSelect={() => selectKpi("all")}
            selectHint="Filter zurücksetzen"
          />
          {/* The count and the amount below it do NOT cover the same set, so the
              amount names its own. `status_counts` is over every mirrored invoice,
              while the reconciliation money totals accumulate only over invoices
              with a complete tenancy mapping and a counted status. */}
          <BexioKpiFilterCard
            icon={Wallet}
            tone="warn"
            label="Offen"
            value={kpis.openCount ?? "—"}
            hint={
              money
                ? `${fmtMoney(money.open_total, currency)} offen (zugeordnete Rechnungen)`
                : undefined
            }
            active={kpi === "open"}
            onSelect={() => selectKpi("open")}
          />
          <BexioKpiFilterCard
            icon={CheckCircle2}
            tone="good"
            label="Bezahlt"
            value={counts?.paid ?? "—"}
            hint={
              money
                ? `${fmtMoney(money.paid_total_invoice_level, currency)} eingegangen (zugeordnete Rechnungen)`
                : undefined
            }
            active={kpi === "paid"}
            onSelect={() => selectKpi("paid")}
          />
          {/* Clicking this runs the SAME predicate the count came from: the server
              filter `mapping=review` is the exact complement of a complete quad,
              i.e. `unmapped + partial_maybe_invalid`. One definition, two uses. */}
          <BexioKpiFilterCard
            icon={AlertTriangle}
            tone={kpis.review ? "warn" : "neutral"}
            label="Zu prüfen"
            value={kpis.review ?? "—"}
            hint="Zuordnung offen oder unvollständig"
            active={kpi === "review"}
            onSelect={() => selectKpi("review")}
          />
        </section>
      )}

      {/* A KPI card and the list its click opens are the same query — EXCEPT while a
          search is active, because the search stays (losing the reader's term would
          be worse) and the reconciliation endpoint these figures come from takes no
          search term. So "Zu prüfen 13" can sit above a list of 2. Rather than let
          that read as a broken count, the scope is stated; the alternative would be
          four more count queries per keystroke and money totals that cannot be
          search-scoped at all. */}
      {debouncedQ ? (
        <p className="mb-4 text-xs text-muted-foreground">
          Kennzahlen gelten für alle synchronisierten Rechnungen. Die Suche wirkt auf
          die Liste darunter.
        </p>
      ) : null}

      <div className="mb-4">
        <BexioFilterBar
          searchValue={filters.q}
          onSearchChange={(q) => applyFilters({ ...filters, q })}
          searchPlaceholder="Beleg-Nr. oder Titel suchen …"
          searchLabel="Rechnungen suchen"
          searchHint="Die Suche durchsucht Beleg-Nr. und Titel serverseitig über alle Seiten — nicht den Kontaktnamen."
          onReset={() => applyFilters(EMPTY_FILTERS)}
          canReset={isFiltered}
          resultLabel={
            typeof total === "number"
              ? `${total} ${total === 1 ? "Rechnung" : "Rechnungen"}`
              : undefined
          }
        >
          {/* Writes the SAME field the KPI cards write, as a one-element list, so
              activating one clears the other instead of stacking a contradiction
              (KPI "Bezahlt" + dropdown "Entwurf" would return nothing). */}
          <BexioFilterSelect
            label="Status"
            value={filters.status?.length === 1 ? filters.status[0] : ""}
            onChange={(status) =>
              applyFilters({
                ...filters,
                status: status ? [status] : [],
                mapping: "",
              })
            }
            options={STATUS_OPTIONS}
          />
          {/* A SECOND dimension, so it does not clear the status the way the KPI
              cards do: "offene Rechnungen, die es bei Bexio nicht mehr gibt" is a
              question worth asking, and the two filters compose server-side. */}
          <BexioFilterSelect
            label="Bexio-Verfügbarkeit"
            value={filters.providerPresence || ""}
            onChange={(providerPresence) =>
              applyFilters({ ...filters, providerPresence })
            }
            options={PRESENCE_OPTIONS}
          />
                </BexioFilterBar>
      </div>

      <div className="mb-3">
        <BexioActiveFilters
          chips={chips}
          onClearAll={() => applyFilters(EMPTY_FILTERS)}
        />
      </div>

      <section
        aria-label="Rechnungen"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        {loading && rows.length === 0 ? (
          <BexioTableSkeleton columns={7} />
        ) : error ? (
          <BexioTableError message={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <BexioTableEmpty
            filtered={queryIsFiltered}
            noun="Rechnungen"
            onReset={() => applyFilters(EMPTY_FILTERS)}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {/* Explicit widths: an auto-layout table hands its spare space
                        to whichever column happens to hold the longest string,
                        which made the contact column swallow the row. */}
                    <th scope="col" className="w-[9%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Beleg-Nr.
                    </th>
                    <th scope="col" className="w-[18%] px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Titel
                    </th>
                    <th scope="col" className="w-[19%] px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Kontakt
                    </th>
                    <th scope="col" className="w-[12%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Status
                    </th>
                    <th scope="col" className="w-[11%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                      Betrag
                    </th>
                    <th scope="col" className="w-[11%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                      Offen
                    </th>
                    <th scope="col" className="w-[9%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Datum
                    </th>
                    <th scope="col" className="w-[11%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Zuordnung
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => (
                    <tr
                      key={inv.id}
                      className={`border-b border-border last:border-0 ${
                        selected?.id === inv.id ? "bg-muted" : ""
                      }`}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5">
                        {/* The row's own control, so the accessible name says
                            which invoice it opens. */}
                        <button
                          type="button"
                          onClick={() => setSelected(inv)}
                          aria-label={`Details zu ${inv.document_nr || `Rechnung ${inv.bexio_invoice_id}`} anzeigen`}
                          className="rounded font-medium tabular-nums text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {inv.document_nr || `#${inv.bexio_invoice_id}`}
                        </button>
                      </td>
                      <td className="truncate px-4 py-2.5 text-foreground">
                        {inv.title || "—"}
                      </td>
                      <td className="truncate px-4 py-2.5 text-muted-foreground">
                        {inv.contact_name || "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {/* Wrapping, not nowrap, on the CELL: the two pills each
                            keep their own `whitespace-nowrap`, and on a narrow
                            column the second drops beneath the first instead of
                            widening the row. */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusPill status={inv.bexio_status} />
                          {isProviderMissing(inv) ? <ProviderPresencePill /> : null}
                        </div>
                      </td>
                      {/* Amounts never truncate. Per-invoice currency is
                          nullable, so these go through `fmtInvoiceMoney`: an
                          invoice the provider never gave us a currency for must
                          not borrow CHF here. The marker rides on the first
                          amount only — the second sits beside it in the same
                          row, so repeating it would say the same thing twice. */}
                      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-foreground">
                        {fmtInvoiceMoney(inv.total, inv.currency)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-foreground">
                        {fmtInvoiceMoney(inv.total_remaining, inv.currency, { marker: false })}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted-foreground">
                        {fmtDate(inv.issue_date)}
                      </td>
                      <td className="px-4 py-2.5">
                        {/* The status is not just information: the action that
                            changes it sits next to it, so an operator does not
                            have to discover that mapping lives inside a drawer. */}
                        <div className="flex items-center gap-2">
                          <MappingPill status={mappingByInvoice[inv.id]?.mapping_status} />
                          <button
                            type="button"
                            onClick={() => setMappingInvoice(inv)}
                            aria-label={`${mappingActionLabel(
                              mappingByInvoice[inv.id]?.mapping_status
                            )}: ${inv.document_nr || `Rechnung ${inv.bexio_invoice_id}`}`}
                            className="shrink-0 whitespace-nowrap rounded-lg border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {mappingActionLabel(mappingByInvoice[inv.id]?.mapping_status)}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <BexioTablePagination
              total={total}
              offset={offset}
              limit={limit}
              onOffsetChange={setOffset}
              onLimitChange={(next) => {
                setLimit(next);
                setOffset(0);
              }}
              noun="Rechnungen"
              busy={loading}
            />
          </>
        )}
      </section>

      {/* ONE mapping implementation for the whole page: the row button and the
          drawer both open this. It is the modal moved out of the legacy page, so
          its 409 handling, its no-preselection rule and its error text are the
          ones already in production. */}
      {mappingInvoice ? (
        <BexioInvoiceMappingModal
          invoice={mappingInvoice}
          onClose={() => setMappingInvoice(null)}
          onChanged={handleMappingChanged}
        />
      ) : null}

      <BexioDetailDrawer
        open={selected != null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
        title={selected?.document_nr || (selected ? `#${selected.bexio_invoice_id}` : "")}
        subtitle={selected?.title || undefined}
        badge={selected ? <StatusPill status={selected.bexio_status} /> : null}
      >
        {selected ? (
          <>
            <DrawerSection title="Rechnung">
              <DrawerField label="Kontakt" value={selected.contact_name} wide />
              {/* The drawer states the currency once, in its own field, so the
                  three amounts carry no marker. `currencyLabel` is what makes
                  that field load-bearing: an unverified currency reads
                  "Unbekannt", never an empty cell that looks like a gap in the
                  form rather than a fact about the invoice. */}
              <DrawerField
                label="Betrag"
                value={fmtInvoiceMoney(selected.total, selected.currency, { marker: false })}
                mono
              />
              <DrawerField
                label="Bezahlt"
                value={fmtInvoiceMoney(selected.total_paid, selected.currency, { marker: false })}
                mono
              />
              <DrawerField
                label="Offen"
                value={fmtInvoiceMoney(selected.total_remaining, selected.currency, {
                  marker: false,
                })}
                mono
              />
              <DrawerField label="Währung" value={currencyLabel(selected.currency)} />
              <DrawerField label="Rechnungsdatum" value={fmtDate(selected.issue_date)} mono />
              <DrawerField label="Fälligkeit" value={fmtDate(selected.due_date)} mono />
              {selected.paid_date ? (
                <DrawerField label="Bezahlt am" value={fmtDate(selected.paid_date)} mono />
              ) : null}
              <DrawerField label="Bexio-ID" value={String(selected.bexio_invoice_id)} mono />
            </DrawerSection>

            {/* Only for a marked invoice. On every other row this section would
                have nothing to say, and a permanently present "Provider-Status:
                unbekannt" would turn the absence of evidence into a finding. */}
            {isProviderMissing(selected) ? (
              <DrawerSection title="Bexio-Verfügbarkeit">
                <DrawerField label="Provider-Status" value={PROVIDER_MISSING_LABEL} wide />
                <DrawerField
                  label="Erkannt am"
                  value={formatDateTime(selected.provider_missing_at)}
                  mono
                />
                {/* Rendered ONLY when a completed inventory actually confirmed the
                    invoice. NULL here means no such confirmation exists — not that
                    the invoice was never at Bexio — so the field is omitted rather
                    than filled with a sentence the data cannot support. */}
                {selected.provider_last_seen_at ? (
                  <DrawerField
                    label="Zuletzt in Bexio bestätigt"
                    value={formatDateTime(selected.provider_last_seen_at)}
                    mono
                  />
                ) : null}
                {/* A div, not a p: DrawerSection puts its children in a <dl>, and
                    only dt/dd/div are valid there. `col-span-2` is the same span
                    DrawerField uses for a wide row. */}
                <div className="col-span-2 text-xs leading-relaxed text-muted-foreground">
                  {PROVIDER_MISSING_EXPLANATION}
                </div>
              </DrawerSection>
            ) : null}

            <DrawerSection title="Zuordnung">
              <DrawerField
                label="Status"
                value={
                  selectedMapping?.mapping_status
                    ? mapLabel(MAPPING_STATUS_LABEL, selectedMapping.mapping_status)
                    : null
                }
                wide
              />
              {/* From the SINGLE-invoice mapping call, not the batch: the batch
                  contract leaves these None by design. Only a resolvable mapping
                  populates them, so absent-after-loading means "not resolvable". */}
              <DrawerField label="Mietverhältnis" value={mappingDetail?.tenancy_reference} wide />
              <DrawerField label="Mieter" value={mappingDetail?.tenant_display_name} wide />
              <DrawerField label="Einheit" value={mappingDetail?.unit_label} />
              <DrawerField label="Zimmer" value={mappingDetail?.room_name} />
              <DrawerField
                label="Mietverhältnis-Status"
                value={mappingDetail?.tenancy_status}
              />
              <DrawerField label="Quelle" value={selected.match_source} />
              {mappingDetailLoading ? (
                <div className="col-span-2 text-xs text-muted-foreground" aria-busy="true">
                  Zuordnungsdetails werden geladen …
                </div>
              ) : null}
              {/* The same action as in the row, reachable from here too.
                  Two reasons, both found in the visual pass: the mapping column is
                  the LAST one, so at 390px it sits outside the table's horizontal
                  scroll until the reader drags to the far edge — the slice's main new
                  affordance, effectively hidden on a phone. And reading the mapping
                  here only to have to close the drawer to act on it is a dead end.
                  The drawer closes as this opens, so two dialogs are never stacked. */}
              <div className="col-span-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    const inv = selected;
                    setSelected(null);
                    setMappingInvoice(inv);
                  }}
                  aria-label={`${mappingActionLabel(
                    selectedMapping?.mapping_status
                  )}: ${selected.document_nr}`}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {mappingActionLabel(selectedMapping?.mapping_status)}
                </button>
              </div>
            </DrawerSection>

            <DrawerSection title="Synchronisation">
              <DrawerField label="Letzter Sync" value={formatDateTime(selected.synced_at)} wide />
              <DrawerField
                label="In Bexio geändert"
                value={selected.bexio_updated_at ? formatDateTime(selected.bexio_updated_at) : null}
                wide
              />
            </DrawerSection>

            <section className="rounded-lg border border-border p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Positionen
              </h3>
              {positionsLoading ? (
                <p className="text-sm text-muted-foreground" aria-busy="true">
                  Positionen werden geladen …
                </p>
              ) : positionsError ? (
                <p className="text-sm text-red-700 dark:text-red-300">{positionsError}</p>
              ) : !positions?.positions?.length ? (
                <p className="text-sm text-muted-foreground">
                  Keine Positionen im Spiegel.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {positions.positions.map((p) => (
                    <li
                      key={`${p.position_index}-${p.bexio_position_id ?? "x"}`}
                      className="flex items-baseline gap-3 py-2 text-sm"
                    >
                      <span className="min-w-0 flex-1 break-words text-foreground">
                        {p.text_plain || p.bexio_unit_name || `Position ${p.position_index + 1}`}
                      </span>
                      {p.amount != null ? (
                        <span className="shrink-0 whitespace-nowrap tabular-nums text-foreground">
                          {fmtInvoiceMoney(p.amount, selected.currency, { marker: false })}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <p className="text-xs text-muted-foreground">
              Schreibgeschützter Spiegel. Bexio ist führend für Status und Zahlung —
              Änderungen erfolgen in Bexio und erscheinen nach der nächsten
              Synchronisation.
            </p>
          </>
        ) : null}
      </BexioDetailDrawer>
    </div>
  );
}
